import React, {createContext, useContext, useState} from 'react';
import {Navigate} from 'react-router-dom';

interface AuthContextValue {
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_KEY = 'cai_authed';

export function AuthProvider({children}: {children: React.ReactNode}): React.ReactElement {
  // Persist across page refreshes via sessionStorage.
  // The HttpOnly cai_session cookie is the real gate (verified in-API by the session
  // authorizer — ADR-1005); this flag just tracks whether the React app has verified
  // the access code in this browser session so it can render the gated views.
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === 'true'
  );

  const login = () => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setIsAuthenticated(true);
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{isAuthenticated, login, logout}}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
throw new Error('useAuth must be used within AuthProvider');
}
  return ctx;
}

export function RequireAuth({children}: {children: React.ReactNode}): React.ReactElement {
  const {isAuthenticated} = useAuth();
  if (!isAuthenticated) {
return <Navigate to="/" replace />;
}
  return <>{children}</>;
}
